import type {
  GeneratedPlan,
  PlanConfig,
} from 'app/pages/NurturingPlanGeneratorPage/types';
import { configToRequestBody } from 'app/pages/NurturingPlanGeneratorPage/utils/planSettingsMapping';
import { header } from 'utils/auth';
import { request } from 'utils/request';

/**
 * Nurturing Plan API service.
 *
 * The plan-generation business logic lives on the backend. On every left-panel
 * input change the UI serialises the config and POSTs it here; the API runs the
 * engine and returns the fully computed GeneratedPlan, which the UI renders.
 */
const GENERATE_ENDPOINT = '/api/v2/nurturing-plan/generate';

const jsonHeaders = () =>
  new Headers({
    'Content-Type': 'application/json',
    Authorization: header(),
  });

/**
 * Generate a nurturing plan for the currently selected dealer.
 *
 * @param config   Plan configuration from the UI (all left-panel inputs).
 * @param dealerId Selected dealer id — sent as the `dealer_id` query param.
 */
export async function generateNurturingPlan(
  config: PlanConfig,
  dealerId?: string,
): Promise<GeneratedPlan> {
  const body = configToRequestBody(config);
  const url = dealerId
    ? `${GENERATE_ENDPOINT}?dealer_id=${dealerId}`
    : GENERATE_ENDPOINT;

  const response: any = await request(url, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  if (!response) {
    throw new Error('Failed to generate nurturing plan');
  }

  const status = response?.statusCode ?? response?.status;
  if (typeof status === 'number' && status >= 400) {
    throw new Error(response?.message || 'Failed to generate nurturing plan');
  }

  return (response?.body ?? response?.data ?? response) as GeneratedPlan;
}

const NurturingPlanService = { generateNurturingPlan };
export default NurturingPlanService;
